import { Box, SxProps, Theme } from '@mui/material';
import React from 'react';
import { getStatusDotMetrics, getStatusPalette, StatusSize } from './status-palette';

export type StatusBadgeSize = StatusSize;

export interface StatusBadgeProps {
  onText: React.ReactNode;
  offText?: React.ReactNode;
  status: boolean;
  /** `small` for cards; `medium` for headers / panorama. */
  size?: StatusBadgeSize;
  className?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

/** Padding, type, and layout metrics per size (no typography shorthand — it brings fontWeight 400). */
function getSizeStyles(size: StatusBadgeSize, theme: Theme) {
  // Pull size/tracking from the variant only; weight is always medium (500) on the badge.
  const type = size === 'medium' ? theme.typography.body2 : theme.typography.caption;
  const dotMetrics = getStatusDotMetrics(size);

  if (size === 'medium') {
    return {
      fontSize: type.fontSize,
      letterSpacing: type.letterSpacing,
      gap: '8px',
      px: 1.75,
      py: 0.75,
      justifyContent: 'center' as const,
      ...dotMetrics,
    };
  }

  return {
    fontSize: type.fontSize,
    letterSpacing: type.letterSpacing,
    gap: '6px',
    px: 1.25,
    py: 0.5,
    justifyContent: 'flex-start' as const,
    ...dotMetrics,
  };
}

/**
 * Online / offline status badge: tinted pill with haloed status dot.
 */
export function StatusBadge({
  status,
  onText,
  offText,
  size = 'small',
  sx,
  className,
  style,
}: StatusBadgeProps) {
  const label = status ? onText : (offText ?? onText);

  return (
    <Box
      component="span"
      role="status"
      className={className}
      style={style}
      sx={[
        (theme) => {
          const palette = getStatusPalette(status, theme.palette.mode);
          const sizeStyles = getSizeStyles(size, theme);
          return {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: sizeStyles.justifyContent,
            gap: sizeStyles.gap,
            // Inherit Rubik (or app font) instead of MUI default Roboto from typography variants.
            fontFamily: 'inherit',
            fontSize: sizeStyles.fontSize,
            letterSpacing: sizeStyles.letterSpacing,
            fontWeight: 500,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            px: sizeStyles.px,
            py: sizeStyles.py,
            borderRadius: '20px',
            backgroundColor: palette.bg,
            color: palette.fg,
            flexShrink: 0,
            '& .StatusBadge-dot': {
              width: sizeStyles.dot,
              height: sizeStyles.dot,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: palette.dot,
              boxShadow: `0 0 0 ${sizeStyles.ring}px ${palette.ring}`,
            },
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box className="StatusBadge-dot" component="span" aria-hidden={true} />
      {label}
    </Box>
  );
}
